import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Form,
  Modal,
  Spinner,
  Tabs,
  Tab
} from "react-bootstrap";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  addDoc
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";
import { FaUserTag, FaHandPointer, FaUndo, FaSearch, FaExchangeAlt, FaTools, FaHammer, FaUserClock, FaFilePdf, FaCheckCircle } from "react-icons/fa";
import { BiTransfer, BiTime, BiCheckCircle, BiDevices, BiWrench, BiSearch } from "react-icons/bi";

const db = getFirestore(firebaseApp);

function GestionPrestamos({ usuario }) {
  const ruc = usuario?.ruc;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("equipos");
  
  // Data States
  const [trabajadores, setTrabajadores] = useState([]);
  const [items, setItems] = useState([]); // Equipos or Herramientas
  const [busqueda, setBusqueda] = useState("");
  const [busquedaTrabajador, setBusquedaTrabajador] = useState("");
  const [trabajadorSeleccionado, setTrabajadorSeleccionado] = useState(null);
  
  // Modal States
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState("");

  useEffect(() => {
    if (ruc) {
      cargarTrabajadores();
      cargarItems();
    }
  }, [ruc, activeTab]);

  const cargarTrabajadores = async () => {
    try {
      const q = query(collection(db, "users"), where("rucEmpresa", "==", ruc));
      const snap = await getDocs(q);
      const data = [];
      snap.forEach(doc => data.push({ id: doc.id, ...doc.data() }));
      setTrabajadores(data.sort((a, b) => (a.apellidos || "").localeCompare(b.apellidos || "")));
    } catch (error) {
      console.error("Error al cargar trabajadores:", error);
    }
  };

  const cargarItems = async () => {
    try {
      setLoading(true);
      const collectionName = activeTab === "equipos" ? "equipos_catalogo" : "herramientas_catalogo";
      const rucStr = ruc?.toString();
      const q = query(collection(db, collectionName), where("ruc_empresa", "==", rucStr));
      const snap = await getDocs(q);
      const data = [];
      
      // Obtener constancias PDF asociadas para items prestados
      const qConst = query(collection(db, "epp_constancias"), where("ruc", "==", rucStr));
      const snapConst = await getDocs(qConst);
      const constanciasMap = {};
      snapConst.forEach(doc => {
        const d = doc.data();
        constanciasMap[d.delivery_id] = d.pdf_url;
      });

      for (const document of snap.docs) {
        const d = document.data();
        const itemId = document.id;
        const hasConstancia = d.current_request_id && constanciasMap[d.current_request_id];

        // Sincronización automática: Si ya se firmó pero el estado sigue siendo Disponible
        if (hasConstancia && d.estado === "Disponible") {
          const itemRef = doc(db, collectionName, itemId);
          await updateDoc(itemRef, {
            estado: "Prestado",
            fechaPrestamo: serverTimestamp(),
            // Los campos trabajadorDni y trabajadorNombre ya fueron prefijados al asignar en EntregaActivos.js
          });
          d.estado = "Prestado"; // Actualizamos objeto local para evitar recarga extra
        }

        data.push({ 
          id: itemId, 
          ...d,
          pdf_url: d.current_request_id ? constanciasMap[d.current_request_id] : null
        });
      }
      setItems(data);
    } catch (error) {
      console.error("Error al cargar items:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLoan = (item) => {
    setSelectedItem(item);
    setSelectedWorker("");
    setTrabajadorSeleccionado(null);
    setBusquedaTrabajador("");
    setShowLoanModal(true);
  };

  const confirmarPrestamo = async () => {
    if (!selectedWorker) {
      Swal.fire("Error", "Debe seleccionar un trabajador", "error");
      return;
    }

    const worker = trabajadores.find(t => t.dni === selectedWorker);
    if (!worker) return;

    try {
      setLoading(true);
      const collectionName = activeTab === "equipos" ? "equipos_catalogo" : "herramientas_catalogo";
      const itemRef = doc(db, collectionName, selectedItem.id);

      await updateDoc(itemRef, {
        estado: "Prestado",
        trabajadorDni: worker.dni,
        trabajadorNombre: `${worker.name} ${worker.apellidos}`,
        fechaPrestamo: new Date(),
        asignado_a: worker.dni // Para compatibilidad
      });

      // Registrar en Kardex de préstamos
      await addDoc(collection(db, "prestamos_historial"), {
        itemId: selectedItem.id,
        itemNombre: selectedItem.nombre,
        itemTipo: activeTab,
        workerDni: worker.dni,
        workerNombre: `${worker.name} ${worker.apellidos}`,
        tipo: "prestamo",
        fecha: new Date(),
        ruc_empresa: ruc
      });

      // 1. Obtener Firma del Responsable (el que asigna en la web)
      let firmaResponsable = "";
      try {
        const qFirma = query(
          collection(db, "firmas"),
          where("dni", "==", usuario?.dni || ""),
          where("ruc", "==", ruc),
          where("activo", "==", true)
        );
        const snapFirma = await getDocs(qFirma);
        if (!snapFirma.empty) {
          firmaResponsable = snapFirma.docs[0].data().firmaUrl || "";
        }
      } catch (e) {
        console.error("Error al obtener firma del responsable:", e);
      }

      // 2. Registrar en epp_requests para visibilidad en la App Móvil
      const eppRequestData = {
        created_at: serverTimestamp(),
        completed_at: new Date(),
        responsible_id: usuario?.dni || "admin",
        responsible_signature_url: firmaResponsable, // NUEVO: Firma del que asigna
        worker_id: worker.dni,
        ruc_empresa: ruc,
        ruc: ruc,
        status: "completed",
        firma_url: "",
        items: [{
          id: selectedItem.codigo?.toString() || selectedItem.id,
          name: selectedItem.nombre,
          quantity: 1,
          size: selectedItem.talla || "Standard",
          observations: `Préstamo de ${activeTab === "equipos" ? "Equipo" : "Herramienta"}`,
        }]
      };

      const eppRequestRef = await addDoc(collection(db, "epp_requests"), eppRequestData);
      await updateDoc(eppRequestRef, { id: eppRequestRef.id });

      // Guardar el ID de la solicitud en el item para vincular el PDF después
      await updateDoc(itemRef, {
        current_request_id: eppRequestRef.id
      });

      Swal.fire("¡Éxito!", "Préstamo registrado correctamente", "success");
      setShowLoanModal(false);
      await cargarItems();
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDevolucion = async (item) => {
    const result = await Swal.fire({
      title: "¿Confirmar Devolución?",
      text: `Se marcará el item ${item.nombre} como Disponible`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#f97316",
      confirmButtonText: "Sí, devolver"
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const collectionName = activeTab === "equipos" ? "equipos_catalogo" : "herramientas_catalogo";
        const itemRef = doc(db, collectionName, item.id);

        await updateDoc(itemRef, {
          estado: "Disponible",
          trabajadorDni: "",
          trabajadorNombre: "",
          fechaPrestamo: null,
          asignado_a: "",
          current_request_id: "" // Limpiar el vínculo al devolver
        });

        await addDoc(collection(db, "prestamos_historial"), {
          itemId: item.id,
          itemNombre: item.nombre,
          itemTipo: activeTab,
          workerDni: item.trabajadorDni,
          workerNombre: item.trabajadorNombre,
          tipo: "devolucion",
          fecha: new Date(),
          ruc_empresa: ruc
        });

        Swal.fire("¡Éxito!", "Devolución registrada correctamente", "success");
        await cargarItems();
      } catch (error) {
        Swal.fire("Error", error.message, "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const itemsFiltrados = items.filter(item => 
    item.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
    item.codigo?.toString().includes(busqueda) ||
    item.trabajadorNombre?.toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <div className="min-vh-100 py-4" style={{ backgroundColor: "#f8f9fa" }}>
      <Container fluid className="px-lg-5 px-3">
        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
          <Card.Header 
            className="text-white py-4 position-relative" 
            style={{ 
              background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
              borderBottom: "3px solid #ea580c"
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaExchangeAlt className="me-3" />
                Gestión de Préstamos y Devoluciones
              </h2>
              <p className="mb-0 opacity-75">Control centralizado de activos reutilizables por colaborador</p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Dashboard Mini de Préstamos */}
            <Row className="mb-4 g-3">
              <Col md={4}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="d-flex align-items-center p-3" style={{ background: "white", borderRadius: "15px" }}>
                    <div className="rounded-3 p-3 bg-orange-light text-orange me-3" style={{ backgroundColor: "rgba(249,115,22,0.1)", color: "#f97316" }}>
                      <BiDevices size={24} />
                    </div>
                    <div>
                      <h5 className="fw-bold mb-0 text-dark">Equipos</h5>
                      <small className="text-muted">Gestión de activos tecnológicos</small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="d-flex align-items-center p-3" style={{ background: "white", borderRadius: "15px" }}>
                    <div className="rounded-3 p-3 bg-orange-light text-orange me-3" style={{ backgroundColor: "rgba(249,115,22,0.1)", color: "#f97316" }}>
                      <BiWrench size={24} />
                    </div>
                    <div>
                      <h5 className="fw-bold mb-0 text-dark">Herramientas</h5>
                      <small className="text-muted">Equipos de trabajo manual</small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="d-flex align-items-center p-3" style={{ background: "white", borderRadius: "15px" }}>
                    <div className="rounded-3 p-3 bg-orange-light text-orange me-3" style={{ backgroundColor: "rgba(249,115,22,0.1)", color: "#f97316" }}>
                      <FaUserClock size={24} />
                    </div>
                    <div>
                      <h5 className="fw-bold mb-0 text-dark">Pendientes</h5>
                      <small className="text-muted">Items actualmente prestados</small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-4 custom-tabs"
              justify
            >
              <Tab eventKey="equipos" title={<span><i className="fas fa-tools me-2"></i>Equipos</span>} />
              <Tab eventKey="herramientas" title={<span><i className="fas fa-hammer me-2"></i>Herramientas</span>} />
            </Tabs>

            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-3">
                <Row className="g-3 align-items-center">
                  <Col md={9}>
                    <div className="position-relative">
                      <BiSearch className="position-absolute" style={{ left: "15px", top: "50%", transform: "translateY(-50%)", color: "#6c757d", fontSize: "1.2rem" }} />
                      <Form.Control
                        type="text"
                        placeholder="Buscar por nombre de item, código o nombre del colaborador responsable..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="ps-5 form-control-lg border-0 bg-light"
                        style={{ borderRadius: "10px" }}
                      />
                    </div>
                  </Col>
                  <Col md={3} className="text-end">
                    <Badge bg="orange" className="p-2 px-3 text-white" style={{ backgroundColor: "#f97316", borderRadius: "8px" }}>
                      RUC Empresa: {ruc}
                    </Badge>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="table-responsive shadow-sm" style={{ borderRadius: "15px" }}>
              <Table hover className="align-middle mb-0 bg-white">
                <thead className="bg-light">
                  <tr>
                    <th className="py-3 ps-4">Código</th>
                    <th className="py-3">Nombre</th>
                    <th className="py-3">Estado</th>
                    <th className="py-3">Responsable Actual</th>
                    <th className="py-3">Fecha Préstamo</th>
                    <th className="py-3 text-center pe-4">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="text-center py-5"><Spinner animation="border" variant="warning" /></td></tr>
                  ) : itemsFiltrados.length === 0 ? (
                    <tr><td colSpan="6" className="text-center py-5 text-muted">No se encontraron items.</td></tr>
                  ) : (
                    itemsFiltrados.map((item) => (
                      <tr key={item.id}>
                        <td className="ps-4 fw-bold"><code>{item.codigo}</code></td>
                        <td>
                          <div className="fw-bold">{item.nombre}</div>
                          <div className="small text-muted">{item.marca} {item.modelo}</div>
                        </td>
                        <td>
                          <Badge bg={item.estado === "Disponible" ? "success" : item.estado === "Prestado" ? "warning" : "danger"}>
                            {item.estado}
                          </Badge>
                        </td>
                        <td>{item.trabajadorNombre || <span className="text-muted small">---</span>}</td>
                        <td className="small">
                          {item.fechaPrestamo?.toDate ? item.fechaPrestamo.toDate().toLocaleDateString() : "-"}
                        </td>
                        <td className="text-center pe-4">
                            {item.estado === "Disponible" ? (
                              <Button 
                                variant="warning" 
                                size="sm" 
                                className="text-white fw-bold d-flex align-items-center gap-2 mx-auto"
                                onClick={() => handleOpenLoan(item)}
                                style={{ borderRadius: "8px" }}
                              >
                                <FaHandPointer /> Prestar
                              </Button>
                            ) : item.estado === "Prestado" ? (
                              <div className="d-flex justify-content-center gap-2">
                                <Button 
                                  variant="success" 
                                  size="sm" 
                                  className="fw-bold d-flex align-items-center gap-2"
                                  onClick={() => handleDevolucion(item)}
                                  style={{ borderRadius: "8px" }}
                                >
                                  <FaUndo /> Devolver
                                </Button>
                                {item.pdf_url && (
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm" 
                                    className="fw-bold d-flex align-items-center btn-hover-orange"
                                    onClick={() => window.open(item.pdf_url, "_blank")}
                                    style={{ borderRadius: "8px" }}
                                    title="Ver Constancia PDF"
                                  >
                                    <FaFilePdf />
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted small">N/A</span>
                            )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* Modal de Préstamo */}
      <Modal show={showLoanModal} onHide={() => setShowLoanModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center text-dark">
            <div
              className="rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
              style={{ background: "rgba(249, 115, 22, 0.1)", color: "#f97316" }}
            >
              <FaExchangeAlt size={20} />
            </div>
            Registrar Préstamo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="mb-4 p-3 bg-light rounded-3 text-center border">
            <h6 className="text-muted mb-1 text-uppercase small fw-bold">Item a Prestar</h6>
            <h4 className="fw-bold text-orange mb-0">{selectedItem?.nombre}</h4>
            <div className="small text-muted">Código: {selectedItem?.codigo}</div>
          </div>

          {!trabajadorSeleccionado ? (
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold d-flex align-items-center gap-2">
                <FaUserTag /> {selectedItem?.itemType === 'equipo' ? 'Asignar a Colaborador' : 'Seleccionar Trabajador'}
              </Form.Label>
              <div className="position-relative mb-2">
                <FaSearch className="position-absolute" style={{ left: "12px", top: "50%", transform: "translateY(-50%)", color: "#6c757d" }} />
                <Form.Control
                  type="text"
                  placeholder="Buscar por DNI o Nombre..."
                  className="ps-5 border-2"
                  value={busquedaTrabajador}
                  onChange={(e) => setBusquedaTrabajador(e.target.value)}
                  autoFocus
                />
              </div>

              {busquedaTrabajador.length > 1 && (
                <div className="border rounded-3 bg-white shadow-sm overflow-auto" style={{ maxHeight: "250px" }}>
                  {trabajadores
                    .filter(t => 
                      t.dni?.toString().includes(busquedaTrabajador) || 
                      t.name?.toLowerCase().includes(busquedaTrabajador.toLowerCase()) ||
                      t.apellidos?.toLowerCase().includes(busquedaTrabajador.toLowerCase())
                    )
                    .map((trab) => (
                      <div 
                        key={trab.dni} 
                        className="p-3 border-bottom list-group-item-action cursor-pointer d-flex align-items-center justify-content-between"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setTrabajadorSeleccionado(trab);
                          setSelectedWorker(trab.dni);
                        }}
                      >
                        <div>
                          <div className="fw-bold text-dark">{trab.apellidos}, {trab.name}</div>
                          <div className="text-muted xx-small d-flex flex-wrap gap-2">
                            <span>DNI: {trab.dni}</span>
                            <span>• {trab.cargo || 'Sin cargo'}</span>
                            <span className="text-orange">• {trab.cecoNombre || 'Sin CECO'}</span>
                          </div>
                        </div>
                        <Button variant="outline-warning" size="sm" className="rounded-pill px-3">Elegir</Button>
                      </div>
                    ))
                  }
                  {trabajadores.length > 0 && trabajadores.filter(t => 
                    t.dni?.toString().includes(busquedaTrabajador) || 
                    t.name?.toLowerCase().includes(busquedaTrabajador.toLowerCase()) ||
                    t.apellidos?.toLowerCase().includes(busquedaTrabajador.toLowerCase())
                  ).length === 0 && (
                    <div className="p-4 text-center text-muted">No se encontraron resultados.</div>
                  )}
                </div>
              )}
            </Form.Group>
          ) : (
            <Card className="border-warning bg-warning bg-opacity-10 mb-4 shadow-sm" style={{ borderRadius: "12px" }}>
              <Card.Body className="p-3">
                <div className="d-flex align-items-center gap-3">
                  <div 
                    className="rounded-circle bg-warning text-white d-flex align-items-center justify-content-center fw-bold"
                    style={{ width: "45px", height: "45px", fontSize: "1.2rem" }}
                  >
                    {trabajadorSeleccionado.name?.charAt(0)}
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="fw-bold mb-0 text-dark">{trabajadorSeleccionado.apellidos}, {trabajadorSeleccionado.name}</h6>
                    <div className="small text-muted">{trabajadorSeleccionado.cargo || 'Colaborador'}</div>
                    <div className="small fw-bold text-orange">{trabajadorSeleccionado.cecoNombre || 'Sede General'}</div>
                  </div>
                  <Button 
                    variant="link" 
                    className="text-danger p-0 text-decoration-none small"
                    onClick={() => {
                      setTrabajadorSeleccionado(null);
                      setSelectedWorker("");
                      setBusquedaTrabajador("");
                    }}
                  >
                    Cambiar
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}

          <Button 
            variant="warning" 
            className="w-100 py-3 text-white fw-bold shadow-lg mt-2" 
            style={{ borderRadius: "12px", background: "#f97316" }}
            onClick={confirmarPrestamo}
            disabled={loading || !selectedWorker}
          >
            {loading ? <Spinner size="sm" className="me-2" /> : <FaCheckCircle className="me-2" />}
            {loading ? "PROCESANDO..." : "CONFIRMAR PRÉSTAMO"}
          </Button>
        </Modal.Body>
      </Modal>

      <style>{`
        .custom-tabs .nav-link {
          color: #6c757d;
          font-weight: bold;
          border: none;
          padding: 15px;
          border-bottom: 3px solid transparent;
        }
        .custom-tabs .nav-link.active {
          color: #f97316 !important;
          background: transparent !important;
          border-bottom: 3px solid #f97316 !important;
        }
        .text-orange { color: #ea580c; }
      `}</style>
    </div>
  );
}

export default GestionPrestamos;
